import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { appTheme, useThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import type { ThemeColors } from '../../shared/theme/store';
import { useTranslation, formatNumber } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import { DASH_STUDIO_RADIUS, studioCardShell } from '../../shared/theme/studio/dashboardConstants';
import { getTodayDateLabel } from '../../data/domains/calendar';

export interface CalendarTodayMacrosCardProps {
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
}

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    container: {
      width: '100%' as const,
      alignSelf: 'stretch' as const,
      borderRadius: DASH_STUDIO_RADIUS,
      backgroundColor: colors.backgroundElevated,
    },
    innerClip: { borderRadius: DASH_STUDIO_RADIUS, overflow: 'hidden' as const },
    contentBody: {
      paddingHorizontal: appTheme.spacing.lg,
      paddingTop: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.md,
      backgroundColor: colors.surface,
    },
    headingRow: {
      flexDirection: 'row',
      alignItems: 'baseline',
      flexWrap: 'wrap',
      gap: appTheme.spacing.xs,
      marginBottom: appTheme.spacing.md,
    },
    dateLabel: {
      fontSize: 15,
      fontWeight: '800',
      color: colors.text,
      letterSpacing: -0.12,
    },
    headingSep: {
      fontSize: 13,
      fontWeight: '600',
      color: colors.textLight,
    },
    macrosLabel: {
      fontSize: 13,
      fontWeight: '700',
      color: colors.textSecondary,
      letterSpacing: 0.15,
    },
    macroPanel: {
      flexDirection: 'row',
      alignItems: 'stretch',
      backgroundColor: (hexToRgba(colors.text, 0.04) as string) || colors.surfaceLight,
      borderRadius: 12,
      overflow: 'hidden' as const,
    },
    caloriesBox: {
      flex: 1.15,
      minWidth: 0,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: (hexToRgba(colors.primary, 0.1) as string) || colors.primaryFade,
      paddingVertical: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.xs,
    },
    macroLabel: {
      fontSize: 8,
      fontWeight: '700',
      color: colors.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: 0.45,
      textAlign: 'center',
    },
    calmacroLabel: { marginBottom: 4 },
    caloriesValue: {
      fontSize: 18,
      fontWeight: '800',
      color: colors.primary,
      fontVariant: ['tabular-nums'] as any,
      textAlign: 'center',
    },
    secondaryMacros: {
      flex: 3,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-evenly',
    },
    miniMacroItem: {
      flex: 1,
      minWidth: 0,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.xxs,
    },
    miniMacroValue: {
      fontSize: 12,
      fontWeight: '800',
      color: colors.text,
      fontVariant: ['tabular-nums'] as any,
    },
    unitText: { fontSize: 10, fontWeight: '600', color: colors.textSecondary },
  });
}

const MacroItem = ({ label, value, styles }: { label: string; value: number; styles: ReturnType<typeof createStyles> }) => (
  <View style={styles.miniMacroItem}>
    <Text style={styles.macroLabel}>{label}</Text>
    <Text style={styles.miniMacroValue} numberOfLines={1}>
      {formatNumber(Math.round(value))}
      <Text style={styles.unitText}> g</Text>
    </Text>
  </View>
);

export const CalendarTodayMacrosCard = React.memo(function CalendarTodayMacrosCard({
  calories,
  protein,
  carbs,
  fats,
}: CalendarTodayMacrosCardProps) {
  const { t, locale } = useTranslation();
  const rtl = useRtl();
  const themeColors = useThemeColors();
  const palette = themeColors ?? appTheme.colors;
  const styles = useMemo(() => createStyles(palette), [palette]);
  const writing = rtl.writingDirection;
  const text_align = rtl.textAlign;
  const today_label = getTodayDateLabel(locale);

  return (
    <View style={[styles.container, studioCardShell(palette)]}>
      <View style={styles.innerClip}>
        <View style={styles.contentBody}>
          <View
            style={styles.headingRow}
            accessibilityRole="header">
            <Text style={[styles.dateLabel, { writingDirection: writing, textAlign: text_align }]} numberOfLines={2}>
              {today_label}
            </Text>
            <Text style={[styles.headingSep, { writingDirection: writing }]} accessibilityElementsHidden>
              ·
            </Text>
            <Text style={[styles.macrosLabel, { writingDirection: writing, textAlign: text_align }]} numberOfLines={1}>
              {t('dashboard.todaysMacros')}
            </Text>
          </View>

          <View style={styles.macroPanel}>
            <View style={styles.caloriesBox}>
              <Text style={[styles.macroLabel, styles.calmacroLabel]}>{t('dashboard.caloriesLabel')}</Text>
              <Text style={styles.caloriesValue}>{formatNumber(Math.round(calories))}</Text>
            </View>
            <View style={styles.secondaryMacros}>
              <MacroItem label={t('dashboard.protein')} value={protein} styles={styles} />
              <MacroItem label={t('dashboard.carbs')} value={carbs} styles={styles} />
              <MacroItem label={t('dashboard.fat')} value={fats} styles={styles} />
            </View>
          </View>
        </View>
      </View>
    </View>
  );
});
